// Language helpers shared by the login payload, the dropdown switcher, and the
// app bootstrap. The app ships en/ru/ua i18n bundles (lowercase codes used by
// ngx-translate); the BE persists an uppercase enum EN | RU | UA | CH. CH is
// never emitted from here — there is no Chinese bundle — but it's part of the
// type so the contract matches the BE.

export type LanguageEnum = 'EN' | 'RU' | 'UA' | 'CH';

// ngx-translate bundle codes we ship.
export type LanguageCode = 'en' | 'ru' | 'ua';

const CODE_TO_ENUM: Record<LanguageCode, LanguageEnum> = {
  en: 'EN',
  ru: 'RU',
  ua: 'UA',
};

// Telegram passes ISO 639-1 ('uk' for Ukrainian); our bundle code is 'ua'.
// Returns a lowercase bundle code or null when Telegram's locale isn't one we
// support.
export function telegramLanguageCode(): LanguageCode | null {
  const code = (typeof window !== 'undefined'
    && (window as any).Telegram?.WebApp?.initDataUnsafe?.user?.language_code) || '';
  const lower = code.toLowerCase().slice(0, 2);
  if (lower === 'uk' || lower === 'ua') return 'ua';
  if (lower === 'ru') return 'ru';
  if (lower === 'en') return 'en';
  return null;
}

// Maps a bundle code (or any loose string) to the BE enum, or null if unknown.
export function toLanguageEnum(code: string | null | undefined): LanguageEnum | null {
  const lower = (code ?? '').toLowerCase();
  return (CODE_TO_ENUM as Record<string, LanguageEnum>)[lower] ?? null;
}

// The language to send on every POST /users/login. Per the BE contract, login
// is authoritative and overwrites the stored value, so we must always send the
// user's current language: the persisted dropdown choice (localStorage 'lang')
// if present, else the Telegram locale. Defaults to EN when nothing resolves
// (e.g. first launch with an unsupported Telegram locale and no stored choice),
// which matches the BE's own default.
export function resolveLoginLanguage(): LanguageEnum {
  const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('lang') : null;
  return toLanguageEnum(stored) ?? toLanguageEnum(telegramLanguageCode()) ?? 'EN';
}
